import React, { useEffect, useState } from 'react';
import { leaveService } from '../services/leaveService';
import { payrollService } from '../services/payrollService';
import './SelfServicePortal.css';

const defaultLeaveForm = {
  leaveType: 'ANNUAL',
  startDate: '',
  endDate: '',
  reason: ''
};

const SelfServicePortal = () => {
  const [leaves, setLeaves] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [leaveForm, setLeaveForm] = useState(defaultLeaveForm);
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadLeaves();
    loadPayrolls();
  }, []);

  const loadLeaves = async () => {
    try {
      const response = await leaveService.getMyLeaves();
      let data = response.data || [];
      // Trier par date de début décroissante (plus récent en premier)
      data.sort((a, b) => {
        const dateA = a.startDate ? new Date(a.startDate) : new Date(0);
        const dateB = b.startDate ? new Date(b.startDate) : new Date(0);
        return dateB - dateA; // Décroissant
      });
      setLeaves(data);
    } catch (err) {
      console.error('Erreur chargement congés', err);
      setError('Impossible de récupérer vos demandes de congés.');
    }
  };

  const loadPayrolls = async () => {
    try {
      const response = await payrollService.getMyPayrolls();
      let data = response.data || [];
      // Trier par période décroissante (année puis mois, plus récent en premier)
      data.sort((a, b) => {
        // Comparer d'abord l'année
        if (a.periodYear !== b.periodYear) {
          return (b.periodYear || 0) - (a.periodYear || 0); // Décroissant
        }
        // Si même année, comparer le mois
        return (b.periodMonth || 0) - (a.periodMonth || 0); // Décroissant
      });
      setPayrolls(data);
    } catch (err) {
      console.error('Erreur chargement paies', err);
      setError('Impossible de récupérer vos bulletins de paie.');
    }
  };

  const handleLeaveInputChange = (e) => {
    const { name, value } = e.target;
    setLeaveForm(prev => ({ ...prev, [name]: value }));
  };

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!leaveForm.startDate || !leaveForm.endDate) {
      setError('Merci de renseigner les dates de début et de fin.');
      return;
    }
    if (new Date(leaveForm.endDate) < new Date(leaveForm.startDate)) {
      setError('La date de fin doit être postérieure à la date de début.');
      return;
    }

    setLeaveSubmitting(true);
    try {
      await leaveService.createMyLeave(leaveForm);
      setSuccess('Demande de congé envoyée avec succès.');
      setLeaveForm(defaultLeaveForm);
      loadLeaves();
    } catch (err) {
      const message = err.response?.data?.error || 'Erreur lors de l\'envoi de la demande.';
      setError(message);
    } finally {
      setLeaveSubmitting(false);
    }
  };

  const downloadPayroll = async (id) => {
    try {
      await payrollService.downloadMyPdf(id);
    } catch (err) {
      const message = err.response?.data?.error || 'Erreur lors du téléchargement.';
      setError(message);
    }
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('fr-FR');
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '0,000 TND';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(value);
  };

  return (
    <div className="self-portal">
      <h1>Mon espace RH</h1>
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}
      {success && (
        <div className="success-banner">
          <span>{success}</span>
          <button onClick={() => setSuccess('')}>×</button>
        </div>
      )}

      <section className="portal-section">
        <div className="section-header">
          <h2>Mes demandes de congé</h2>
        </div>
        <div className="section-content">
          <form className="leave-form" onSubmit={handleLeaveSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Type de congé</label>
                <select
                  name="leaveType"
                  value={leaveForm.leaveType}
                  onChange={handleLeaveInputChange}
                >
                  <option value="ANNUAL">Congé annuel</option>
                  <option value="SICK">Congé maladie</option>
                  <option value="UNPAID">Sans solde</option>
                  <option value="PERSONAL">Congé spéciaux</option>
                  <option value="OTHER">Autre</option>
                </select>
              </div>
              <div className="form-group">
                <label>Date de début</label>
                <input
                  type="date"
                  name="startDate"
                  value={leaveForm.startDate}
                  onChange={handleLeaveInputChange}
                />
              </div>
              <div className="form-group">
                <label>Date de fin</label>
                <input
                  type="date"
                  name="endDate"
                  value={leaveForm.endDate}
                  onChange={handleLeaveInputChange}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Raison (optionnel)</label>
              <textarea
                name="reason"
                rows="2"
                value={leaveForm.reason}
                onChange={handleLeaveInputChange}
                placeholder="Expliquez la raison de votre demande..."
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={leaveSubmitting}>
                {leaveSubmitting ? 'Envoi...' : 'Soumettre la demande'}
              </button>
            </div>
          </form>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Début</th>
                  <th>Fin</th>
                  <th>Jours</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {leaves.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-cell">Aucune demande pour le moment.</td>
                  </tr>
                ) : (
                  leaves.map((leave) => (
                    <tr key={leave.id}>
                      <td>{leave.leaveType}</td>
                      <td>{formatDate(leave.startDate)}</td>
                      <td>{formatDate(leave.endDate)}</td>
                      <td>{leave.numberOfDays}</td>
                      <td>
                        <span className={`status-badge status-${leave.status?.toLowerCase()}`}>
                          {leave.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="portal-section">
        <div className="section-header">
          <h2>Mes bulletins de paie</h2>
        </div>
        <div className="section-content">
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Période</th>
                  <th>Salaire brut</th>
                  <th>Salaire net</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payrolls.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="empty-cell">Aucun bulletin disponible.</td>
                  </tr>
                ) : (
                  payrolls.map((payroll) => (
                    <tr key={payroll.id}>
                      <td>
                        {payroll.periodMonth && payroll.periodYear
                          ? new Date(payroll.periodYear, payroll.periodMonth - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
                          : '-'}
                      </td>
                      <td>{formatCurrency(payroll.grossSalary)}</td>
                      <td>{formatCurrency(payroll.netSalary)}</td>
                      <td>
                        <span className={`status-badge status-${payroll.status?.toLowerCase()}`}>
                          {payroll.statusLabel || payroll.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn-action btn-download"
                          title="Télécharger le PDF"
                          onClick={() => downloadPayroll(payroll.id)}
                          disabled={!payroll.status || (payroll.status !== 'VALIDATED' && payroll.status !== 'PAID')}
                        >
                          📄 Télécharger
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SelfServicePortal;

